// rtl_power emits naive local timestamps ("2025-11-23, 17:46:04") with no zone
// or offset, so converting to an instant needs the capture host's zone.
//
// Ported from spectrum-recorder's tz.ts. Done with Intl rather than a date
// library; Temporal is not yet everywhere and this is the only date arithmetic
// the app performs.

import type { NaiveTime } from './units.ts'

export class TimeZoneError extends Error {
  override name = 'TimeZoneError'
}

const formatters = new Map<string, Intl.DateTimeFormat>()

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone)
  if (formatter) return formatter
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      era: 'short',
    })
  } catch {
    throw new TimeZoneError(`unknown IANA time zone: ${JSON.stringify(timeZone)}`)
  }
  formatters.set(timeZone, formatter)
  return formatter
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    formatterFor(timeZone)
    return true
  } catch {
    return false
  }
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/** The wall clock `timeZone` shows at `epochMs`, as calendar fields. */
export function wallClock(epochMs: number, timeZone: string): NaiveTime {
  const parts = formatterFor(timeZone).formatToParts(new Date(epochMs))
  const field: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {}
  for (const part of parts) field[part.type] = part.value
  const year = Number(field.year)
  return {
    year: field.era === 'BC' ? 1 - year : year,
    month: Number(field.month),
    day: Number(field.day),
    hour: Number(field.hour),
    minute: Number(field.minute),
    second: Number(field.second),
  }
}

function naiveAsUtc(naive: NaiveTime): number {
  const ms = Date.UTC(naive.year, naive.month - 1, naive.day, naive.hour, naive.minute, naive.second)
  if (naive.year >= 0 && naive.year < 100) {
    const shifted = new Date(ms)
    shifted.setUTCFullYear(naive.year)
    return shifted.getTime()
  }
  return ms
}

function wallClockUtcMs(epochMs: number, timeZone: string): number {
  return naiveAsUtc(wallClock(epochMs, timeZone))
}

/**
 * Resolves a naive wall-clock time in `timeZone` to an epoch instant.
 *
 * Two passes: guess the offset by reading it at the naive value treated as
 * UTC, apply it, then re-read the offset at the corrected instant, which is
 * what makes DST transitions come out right. A repeated fall-back hour resolves
 * to its first occurrence so a capture stays monotonic; a nonexistent
 * spring-forward time shifts forward across the gap.
 */
export function zonedNaiveToEpochMs(naive: NaiveTime, timeZone: string): number {
  const asUtc = naiveAsUtc(naive)
  const firstGuess = wallClockUtcMs(asUtc, timeZone) - asUtc
  const candidate = asUtc - firstGuess
  const refinedOffset = wallClockUtcMs(candidate, timeZone) - candidate
  if (refinedOffset === firstGuess) return candidate
  const refined = asUtc - refinedOffset
  return wallClockUtcMs(refined, timeZone) === asUtc ? refined : candidate
}

/** Epoch ms of the start of the hour containing `epochMs`, in `timeZone`. */
export function floorToHour(epochMs: number, timeZone: string): number {
  const wall = wallClock(epochMs, timeZone)
  return zonedNaiveToEpochMs({ ...wall, minute: 0, second: 0 }, timeZone)
}

/** Epoch ms of midnight starting the day containing `epochMs`, in `timeZone`. */
export function floorToDay(epochMs: number, timeZone: string): number {
  const wall = wallClock(epochMs, timeZone)
  return zonedNaiveToEpochMs({ ...wall, hour: 0, minute: 0, second: 0 }, timeZone)
}

const pad2 = (n: number) => String(n).padStart(2, '0')

export type ClockFormat = 'date' | 'hm' | 'hms' | 'full'

/** Formats an instant in `timeZone` the way heatmap.py's strftime calls do. */
export function formatClock(epochMs: number, timeZone: string, format: ClockFormat): string {
  const w = wallClock(epochMs, timeZone)
  const date = `${w.year}-${pad2(w.month)}-${pad2(w.day)}`
  const hm = `${pad2(w.hour)}:${pad2(w.minute)}`
  switch (format) {
    case 'date':
      return date
    case 'hm':
      return hm
    case 'hms':
      return `${hm}:${pad2(w.second)}`
    case 'full':
      return `${date} ${hm}:${pad2(w.second)}${timeZone === 'UTC' ? 'Z' : ''}`
  }
}
